import { Field, useFormikContext } from 'formik'

import { Box } from 'app/components/design-system-components'
import { DateTimeField } from 'app/components/design-system-components/inputs'
import { CheckboxInputField } from 'app/components/design-system-components/inputs/Checkboxes/Checkbox'
import { SelectField } from 'app/components/design-system-components/inputs/Select/Select'
import { FieldWrapper } from 'app/forms/FieldWrapper'
import {
  formFields,
  getPropertyArrangementValue,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/form.utils'
import {
  StyledFieldWrapper,
  StyledFlexBox,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/styled-form-component'
import type { RentalHistoryFormValues } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import {
  isAddressHistoryOwned,
  isAddressHistoryTemporaryBnBArrangement,
  isAddressHistoryWithFamilyOrFriends,
} from 'app/sm/helpers'

interface DateSectionProps {
  values: RentalHistoryFormValues
}

// Reason for leaving options based on existing code
const REASON_FOR_LEAVING_OPTIONS = [
  {
    option: { value: 'Sold by owner', label: 'Sold by owner' },
    optionMeta: {
      id: 'sold_by_owner',
      displayText: 'Sold by owner',
      value: 'Sold by owner',
    },
  },
  {
    option: { value: 'Owner moving in', label: 'Owner moving in' },
    optionMeta: {
      id: 'owner_moving_in',
      displayText: 'Owner moving in',
      value: 'Owner moving in',
    },
  },
  {
    option: { value: 'Moved for work', label: 'Moved for work' },
    optionMeta: {
      id: 'moved_for_work',
      displayText: 'Moved for work',
      value: 'Moved for work',
    },
  },
  {
    option: {
      value: 'Property was too expensive',
      label: 'Property was too expensive',
    },
    optionMeta: {
      id: 'too_expensive',
      displayText: 'Property was too expensive',
      value: 'Property was too expensive',
    },
  },
  {
    option: { value: 'Bigger place required', label: 'Bigger place required' },
    optionMeta: {
      id: 'bigger_place',
      displayText: 'Bigger place required',
      value: 'Bigger place required',
    },
  },
  {
    option: {
      value: 'Smaller place required',
      label: 'Smaller place required',
    },
    optionMeta: {
      id: 'smaller_place',
      displayText: 'Smaller place required',
      value: 'Smaller place required',
    },
  },
  {
    option: { value: 'Personal reasons', label: 'Personal reasons' },
    optionMeta: {
      id: 'personal_reasons',
      displayText: 'Personal reasons',
      value: 'Personal reasons',
    },
  },
  {
    option: { value: 'Tenancy issues', label: 'Tenancy issues' },
    optionMeta: {
      id: 'tenancy_issues',
      displayText: 'Tenancy issues',
      value: 'Tenancy issues',
    },
  },
]

/**
 * Date section component
 * Handles start/end dates, rolling lease, and reason for leaving
 */
export const DateSection = ({ values }: DateSectionProps) => {
  const { setFieldValue } = useFormikContext<RentalHistoryFormValues>()

  const handleRollingLeaseChange = (checked: boolean) => {
    setFieldValue(formFields.isRollingLease, checked)
    if (checked) {
      // Clear end date if rolling lease is selected
      setFieldValue(formFields.endDate, '')
    }
  }

  // const showOtherReasonField = values.reasonForLeaving === 'other'
  const ownedTheProperty = isAddressHistoryOwned(
    getPropertyArrangementValue(values.propertyArrangement),
  )
  const livedWithFamilyOrFriends = isAddressHistoryWithFamilyOrFriends(
    getPropertyArrangementValue(values.propertyArrangement),
  )
  const isTemporaryBnBArrangement = isAddressHistoryTemporaryBnBArrangement(
    getPropertyArrangementValue(values.propertyArrangement),
  )
  const showOptionalLabel =
    livedWithFamilyOrFriends || isTemporaryBnBArrangement
  const isCurrentAddress = values.isCurrentAddress

  return (
    <Box mb={7}>
      <StyledFlexBox gap={3} mb={7}>
        <Box flex={1}>
          {/* Is Current Address Checkbox */}
          <FieldWrapper
            id={formFields.isCurrentAddress}
            name={formFields.isCurrentAddress}
          >
            <Field name={formFields.isCurrentAddress}>
              {({ field }: any) => (
                <CheckboxInputField {...field}>
                  Current residence
                </CheckboxInputField>
              )}
            </Field>
          </FieldWrapper>
        </Box>
        <Box flex={1}></Box>
      </StyledFlexBox>

      {isCurrentAddress && (
        <StyledFlexBox gap={3} mb={7}>
          <Box flex={1}>
            {/* Rolling Lease Checkbox */}
            <FieldWrapper
              id={formFields.isRollingLease}
              name={formFields.isRollingLease}
            >
              <Field name={formFields.isRollingLease}>
                {({ field }: any) => (
                  <CheckboxInputField
                    {...field}
                    changeHandler={handleRollingLeaseChange}
                    label="This was a rolling/periodic lease (no fixed end date)"
                  >
                    {ownedTheProperty || showOptionalLabel
                      ? `I don't have an end date`
                      : `Ongoing / rolling arrangement`}
                  </CheckboxInputField>
                )}
              </Field>
            </FieldWrapper>
          </Box>
          <Box flex={1}></Box>
        </StyledFlexBox>
      )}

      <StyledFlexBox gap={3} mb={7}>
        <Box flex={1}>
          {/* Start Date */}
          <StyledFieldWrapper
            id={formFields.startDate}
            name={formFields.startDate}
            label="Start Date"
            style={{ position: 'relative' }}
            required
          >
            <DateTimeField
              id={formFields.startDate}
              name={formFields.startDate}
              placeholder="Select date"
              dateFormat="DD/MM/YYYY"
              timeFormat={false}
            />
          </StyledFieldWrapper>
        </Box>
        <Box flex={1}>
          {/* End Date - Only show if not rolling lease */}
          {!values.isRollingLease && (
            <StyledFieldWrapper
              id={formFields.endDate}
              name={formFields.endDate}
              label="End Date"
              style={{ position: 'relative' }}
              required
            >
              <DateTimeField
                id={formFields.endDate}
                name={formFields.endDate}
                placeholder="Select date"
                dateFormat="DD/MM/YYYY"
                timeFormat={false}
              />
            </StyledFieldWrapper>
          )}
        </Box>
      </StyledFlexBox>

      {!values.isQLDForm22 && !isCurrentAddress && (
        <StyledFlexBox flexDirection="column">
          {/* Reason for Leaving */}
          <StyledFieldWrapper
            id={formFields.reasonForLeaving}
            name={formFields.reasonForLeaving}
            label="Reason for Leaving"
            required
          >
            <Field name={formFields.reasonForLeaving}>
              {({ field }: any) => (
                <SelectField
                  {...field}
                  options={REASON_FOR_LEAVING_OPTIONS}
                  label="Select reason for leaving"
                />
              )}
            </Field>
          </StyledFieldWrapper>
        </StyledFlexBox>
      )}
    </Box>
  )
}
